import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { rectSortingStrategy, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { Maximize2, Minus, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { blockColumns } from '../lib/surumMutations'
import type { DocBlock, FieldColumns, SablonSurum } from '../types'
import { CanvasBlock } from './CanvasBlock'
import { DocLetterhead } from './DocLetterhead'
import { DocumentToolbar } from './DocumentToolbar'
import { SectionDropZone, SortableBlock } from './SortableBlock'

const SPAN: Record<FieldColumns, string> = { 1: 'col-span-6', 2: 'col-span-3', 3: 'col-span-2' }

type Props = {
  surum: SablonSurum
  pageId: string
  selectedFieldId?: string | null
  zoom: number
  onZoomChange: (z: number) => void
  sampleValues?: Record<string, string>
  editable?: boolean
  showToolbar?: boolean
  onSelectField?: (id: string | null) => void
  onUpdateBlock?: (sectionId: string, block: DocBlock) => void
  onMoveBlock?: (activeId: string, overId: string) => void
  onRemoveBlock?: (blockId: string) => void
  onUpdateSurumMeta?: (patch: Partial<Pick<SablonSurum, 'editorFont' | 'editorFontSize' | 'lineHeight'>>) => void
  fillMode?: 'empty' | 'sample'
}

export function DocumentCanvas({
  surum,
  pageId,
  selectedFieldId,
  zoom,
  onZoomChange,
  sampleValues,
  editable = true,
  showToolbar = editable,
  onSelectField,
  onUpdateBlock,
  onMoveBlock,
  onRemoveBlock,
  onUpdateSurumMeta,
  fillMode = 'empty',
}: Props) {
  const { t } = useTranslation()
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const page = surum.pages.find((p) => p.id === pageId) ?? surum.pages[0]
  const sections = surum.sections.filter((s) => s.pageId === (page?.id ?? pageId)).sort((a, b) => a.order - b.order)
  const fieldMap = new Map(surum.fields.map((f) => [f.id, f]))
  const pageInfo = { current: surum.pages.findIndex((p) => p.id === page?.id) + 1, total: Math.max(surum.pages.length, 1) }
  const sortable = editable && !!onMoveBlock
  const blockIds = sections.flatMap((s) => (surum.blocksBySection[s.id] ?? []).map((b) => b.id))

  function onDragEnd({ active, over }: DragEndEvent) {
    if (over && active.id !== over.id) onMoveBlock?.(String(active.id), String(over.id))
  }

  const body = (
    <div className="space-y-4">
      {sections.map((sec) => {
        const blocks = surum.blocksBySection[sec.id] ?? []
        return (
          <SectionDropZone key={sec.id} sectionId={sec.id} empty={sortable && blocks.length === 0}>
            {blocks.map((block) => {
              const columns = blockColumns(block)
              const node = (
                <CanvasBlock
                  block={block}
                  fieldMap={fieldMap}
                  selectedFieldId={selectedFieldId}
                  sampleValues={sampleValues}
                  fillMode={fillMode}
                  editable={editable}
                  surum={surum}
                  onSelectField={onSelectField}
                  onUpdateBlock={(b) => onUpdateBlock?.(sec.id, b)}
                  onUpdateSurumMeta={onUpdateSurumMeta}
                />
              )
              return sortable ? (
                <SortableBlock
                  key={block.id}
                  id={block.id}
                  zoom={zoom}
                  compact={columns > 1}
                  className={SPAN[columns]}
                  removeLabel={t(block.kind === 'field' || block.kind === 'signature' ? 'hr.belgeler.remove.fieldAction' : 'hr.belgeler.remove.blockAction')}
                  onRemove={onRemoveBlock ? () => onRemoveBlock(block.id) : undefined}
                >
                  {node}
                </SortableBlock>
              ) : (
                <div key={block.id} data-block-id={block.id} className={`min-w-0 ${SPAN[columns]}`}>
                  {node}
                </div>
              )
            })}
          </SectionDropZone>
        )
      })}
      {sections.length === 0 ? <p className="text-sm text-muted-foreground">{t('hr.belgeler.canvas.empty')}</p> : null}
    </div>
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {showToolbar && onUpdateSurumMeta ? <DocumentToolbar surum={surum} onMetaChange={onUpdateSurumMeta} /> : null}
      <div className="min-h-0 flex-1 overflow-auto scrollbar-none bg-muted/50 p-3 md:p-6 dark:bg-muted/20">
        <div className="mx-auto origin-top transition-transform" style={{ transform: `scale(${zoom})`, width: '210mm', maxWidth: '100%' }}>
          <article
            className="flex min-h-[297mm] flex-col rounded-[3px] border border-border/60 bg-card shadow-[0_1px_3px_color-mix(in_oklab,var(--shadow-color)_8%,transparent),0_12px_32px_-12px_color-mix(in_oklab,var(--shadow-color)_18%,transparent)]"
            style={{
              padding: `${surum.margins.top}mm ${surum.margins.right}mm ${surum.margins.bottom}mm ${surum.margins.left}mm`,
              fontFamily: surum.editorFont,
              fontSize: `${surum.editorFontSize}px`,
              lineHeight: surum.lineHeight,
            }}
            onClick={() => onSelectField?.(null)}
          >
            <DocLetterhead surum={surum} />
            <div className="flex-1">
              {sortable ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                  <SortableContext items={blockIds} strategy={rectSortingStrategy}>
                    {body}
                  </SortableContext>
                </DndContext>
              ) : (
                body
              )}
            </div>
          </article>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/70 bg-card px-3 py-1">
        <span className="text-[11px] text-muted-foreground">{t('hr.belgeler.canvas.pageOf', pageInfo)}</span>
        <div className="flex items-center gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={t('hr.belgeler.canvas.zoomOut')}
            onClick={() => onZoomChange(Math.max(0.6, Math.round((zoom - 0.1) * 10) / 10))}
          >
            <Minus />
          </Button>
          <span className="w-10 text-center text-[11px] tabular-nums">{Math.round(zoom * 100)}%</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={t('hr.belgeler.canvas.zoomIn')}
            onClick={() => onZoomChange(Math.min(1.4, Math.round((zoom + 0.1) * 10) / 10))}
          >
            <Plus />
          </Button>
          <Button type="button" variant="ghost" size="icon" className="size-7" aria-label={t('hr.belgeler.canvas.zoomReset')} onClick={() => onZoomChange(1)}>
            <Maximize2 />
          </Button>
        </div>
      </div>
    </div>
  )
}
